import { useAccount } from "@/account/useAccount";
import { PremiumDataModal } from "@/components/PremiumDataModal/PremiumDataModal";
import { usePremiumDataModal } from "@/components/PremiumDataModal/hooks/usePremiumDataModal";
import { useAppConfigContext } from "@/context/AppConfig";
import { useAppConstantsContext } from "@/context/AppConstants";
import { PremiumPaymentSuccessModal } from "@/reports/shared/PremiumPaymentModal/PremiumPaymentSuccessModal";
import { List, type SxProps, styled } from "@/theme/index";
import { checkSingleReportVariations } from "@/utils/checkSingleReportVariations";
import { useState } from "react";
import {
  NestedComponent,
  type SideMenuItem,
  SingleComponent,
} from "./components/SideNavigationMenuItem";

const StyledList = styled(List)(({ theme }) => ({
  ".MuiTypography-root": {
    fontSize: 18,
  },
  "&.on-report": {
    display: "none",
    position: "fixed",
    left: 0,
    width: "50px",
    boxShadow: "0 2px 10px 1px rgb(95 85 99 / 35%)",
    height: "100%",
    whiteSpace: "nowrap",
    overflow: "hidden",
    marginTop: theme.spacing(0),
    top: "50px",
    transition: "0.3s",
    padding: `${theme.spacing(2)} 0 0 0`,
    backgroundColor: theme.palette.background.paper,
    zIndex: 9,

    [theme.breakpoints.up("md")]: {
      display: "block",
    },

    "&:hover": {
      width: "300px",
    },
  },
}));

type routeToHide = "report" | "dashboard";

interface HideItems {
  name: string;
  routesToHide?: routeToHide[];
}

export function SideNavigationMenu({
  menuItems,
  matchingRoute,
  onReport = false,
  hideItems = [],
  sx,
  onNavigationClick,
}: {
  menuItems: SideMenuItem[];
  matchingRoute: string;
  onReport?: boolean;
  hideItems?: HideItems[];
  sx?: SxProps;
  onNavigationClick?: () => void;
}) {
  const { variationPlanNames, internationalPlanNames } =
    useAppConstantsContext();
  const { routingUtils } = useAppConfigContext();
  const { data: account } = useAccount(true);
  const { navigate } = routingUtils;

  const reportVariations =
    account && internationalPlanNames
      ? checkSingleReportVariations(
          account,
          variationPlanNames,
          internationalPlanNames,
        )
      : null;
  const isInSingleReportVariation =
    reportVariations?.isInSingleReportVariation ?? false;

  const { isModalOpen, closeModal, triggerModal } = usePremiumDataModal();
  const [searchType, setSearchType] = useState("");

  const [paymentShowModalNumber, setPaymentShowModalNumber] =
    useState<number>(0);
  const [isSuccessModaVisible, setIsSuccessModaVisible] =
    useState<boolean>(false);

  const openSuccessModal = () => {
    setIsSuccessModaVisible(true);
  };

  const closeModals = () => {
    setIsSuccessModaVisible(false);
    setPaymentShowModalNumber(0);
  };

  const onSuccessPaymentHandler = () => {
    closeModals();
    navigate("/dashboard");
  };

  const filteredMenuItems = menuItems.filter((menuItem) => {
    const currentRoute = onReport ? "report" : "dashboard";
    return !hideItems.some((hideItem) => {
      return (
        hideItem.routesToHide?.includes(currentRoute) &&
        menuItem.name.toLowerCase().includes(hideItem.name.toLowerCase())
      );
    });
  });

  const cleanedSearchType = searchType.replace(/ search$/gi, "");

  const [openItem, setOpenItem] = useState({});

  const handleToggle = (item: SideMenuItem) => {
    setOpenItem((prevOpenItem) => (prevOpenItem === item ? undefined : item));
  };

  return (
    <StyledList
      component="nav"
      sx={{
        width: "100%",
        maxWidth: 284,
        bgcolor: "background.paper",
        padding: 2,
        ...sx,
      }}
      aria-labelledby="nested-list-sidenav"
      className={onReport ? "on-report" : ""}
    >
      {filteredMenuItems.map((item, index, array) => {
        const hasItems = item.childItems?.length;
        const isLastItem = index === array.length - 1;
        return hasItems ? (
          <NestedComponent
            key={`${item.name}-${item.route}`}
            item={item}
            match={matchingRoute}
            triggerModal={() => triggerModal(true, isInSingleReportVariation)}
            isOpen={openItem === item}
            handleToggle={() => handleToggle(item)}
            isLastItem={isLastItem}
            onNavigationClick={onNavigationClick}
          />
        ) : (
          <SingleComponent
            key={`${item.name}-${item.route}`}
            item={item}
            match={matchingRoute}
            triggerModal={() => triggerModal(true, isInSingleReportVariation)}
            setSearchType={setSearchType}
            isLastItem={isLastItem}
            onNavigationClick={onNavigationClick}
          />
        );
      })}
      <PremiumDataModal
        isOpen={isModalOpen}
        onClose={closeModal}
        searchType={cleanedSearchType}
        paymentShowModalNumber={paymentShowModalNumber}
        setPaymentShowModalNumber={setPaymentShowModalNumber}
        openSuccessModal={openSuccessModal}
      />
      <PremiumPaymentSuccessModal
        open={isSuccessModaVisible}
        onClose={closeModals}
        onAccept={onSuccessPaymentHandler}
      />
    </StyledList>
  );
}
